import { useRouter } from 'expo-router';
import { ScrollView } from 'react-native';
import { Paragraph, YStack } from 'tamagui';

import { SolidSurface } from '@/components/glass-surface';
import { SettingsRow, SettingsSection } from '@/components/settings-row';

import { PageEnd } from '@/components/page-end';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { ScreenHeader } from '@/components/screen-header';
import { TextLink } from '@/components/text-link';
import { Text } from '@/components/text';
import { space } from '@/theme/tokens.generated';

// Issue #254: the app's whole premise is meeting strangers in person,
// yet there was no help/support/safety surface anywhere (the formal
// critique's Nielsen-10 score: 1/4). This screen is the single entry
// point -- sober, plain-language copy in the safety-reminder screen's
// register (not the playful noticeboard voice): see that screen's own
// doc comment for why safety content keeps a distinct tone.
//
// The emergency-services disclaimer is design.md's own Incident-response
// requirement (issue #159 tracks the ToS/Privacy siblings); it lives
// here in the strongest form available: its own section, not a footer
// line.
export function SafetyCenter() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();

  return (
    <ScreenContainer scrollUnderDock>
      <YStack>
        <ScreenHeader
          title="Safety Center"
          subtitle="Nearkin works because neighbors stay in control. Here's what that means in practice."
          onBack={() => router.back()}
        />
      </YStack>
      <ScrollView
        style={{ flex: 1 }}
        {...dockScroll}
        contentContainerStyle={{ paddingTop: space[4], paddingBottom: space[8] + dockClearance, gap: space[5] }}
      >
        <YStack gap="$2">
          <Text color="$color" fontWeight="$semibold">
            Meeting someone from here
          </Text>
          <Paragraph color="$colorSecondary">
            Meet in public places, tell someone where you&apos;re going, and take your time. You&apos;re never obligated
            to meet anyone, and most conversations stay right here in the threads.
          </Paragraph>
        </YStack>

        <YStack gap="$2">
          <Text color="$color" fontWeight="$semibold">
            Your identity stays yours
          </Text>
          <Paragraph color="$colorSecondary">
            You&apos;re a handle and an avatar until you and another neighbor both choose to reveal. Your photos stay
            hidden until that mutual choice, and your exact location is never shown to anyone.
          </Paragraph>
        </YStack>

        <YStack gap="$2">
          <Text color="$color" fontWeight="$semibold">
            If someone crosses a line
          </Text>
          <Paragraph color="$colorSecondary">
            Blocking removes them from your view instantly — they can&apos;t see your threads, and they can&apos;t
            request a reveal again. You can unblock someone from here if things change. Severe reports go straight to
            the founder.
          </Paragraph>
          <SettingsSection>
            <SettingsRow label="Review blocked users" onPress={() => router.push({ pathname: '/blocked-users' })} />
            <SettingsRow label="Review reveal requests" onPress={() => router.push({ pathname: '/reveal-requests' })} />
          </SettingsSection>
        </YStack>

        <SolidSurface style={{ padding: space[4] }}>
          <YStack gap="$2">
            <Text color="$color" fontWeight="$semibold">
              In an emergency
            </Text>
            <Paragraph color="$color">
              Nearkin is not an emergency service. If you are in danger, contact your local emergency services first,
              then block and report here so we can act too.
            </Paragraph>
          </YStack>
        </SolidSurface>

        <YStack gap="$2">
          <Text color="$color" fontWeight="$semibold">
            Replaying the safety reminder
          </Text>
          <Paragraph color="$colorSecondary">
            The meet-up safety guidance shown when you connected with someone is always available.
          </Paragraph>
          <TextLink fontSize="$meta" lineHeight="$meta" onPress={() => router.push({ pathname: '/safety-reminder' })}>
            Open the safety reminder
          </TextLink>
        </YStack>
        <PageEnd>That&apos;s the lot. Trust your instincts, and leave when something feels off.</PageEnd>
      </ScrollView>
    </ScreenContainer>
  );
}
